import { useQueryClient } from "@tanstack/react-query";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ChangeLifecycleButtons from "eSign/components/ChangeLifecycleButtons";
import ProductsPanel, { getProductsByParentQueryKey } from "eSign/panelsegment/ProductsPanel";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import { InvoiceV2DraftSection } from "InvoiceV2/components/DocumentDetails/InvoiceV2DraftSection";
import RecipientsPanel from "InvoiceV2/panelsegment/RecipientsPanel";
import React, { useState } from "react";
import TwoColumnLayout from "../../layout/TwoColumnLayout";
import InvoiceActivityPanel from "../components/activity/InvoiceActivityPanel";
import InvoiceV2OptionsPanel from "../components/optionspanel/InvoiceV2OptionsPanel";
import NotesPanel from "note/list/app";
import { isPanelVisible } from "utilities/panelUtils";
import ReadonlyLinkedShowsPanelSegment from "document/linkshow/ReadonlyLinkedShowsPanelSegment";
import { InvoiceReminderApp } from "DocumentReminder/AppForInvoices";

interface Props {
  viewModel: server.viewmodel.InvoiceV2DetailFrontModel
}

const DraftPanel = (props: Props) => {
  const [invoice, setInvoice] = useState(props.viewModel.details);

  const productsByParentQueryKey = getProductsByParentQueryKey("invoice", invoice.invoiceId);

  const queryClient = useQueryClient();

  const onBookkeepingOrCurrencyChange = () => {
    queryClient.invalidateQueries(productsByParentQueryKey);
  }

  const refetchInvoice = async () => {
    const newInvoice = await window.CoreApi.InvoiceV2.GetSidePanelData(props.viewModel.details.invoiceId);
    setInvoice(newInvoice.data.data.invoice);
  }

  const isValid = async () => {
    const result = await validators.validateAll();
    if (result.hasErrors) {
      result.scrollToFirstError();
    }
    return !result.hasErrors;
  };

  const validators = useValidators();

  const validateForPreview = async () => {
    const result = await validators.validateAll();
    const isValid = !result.hasErrors;
    if (!isValid) {
      result.scrollToFirstError();
    }

    return isValid;
  }

  const missingFields = validators
    .validationResult
    .failedKeys
    .map(e => e.title);

  return <ValidatorContext settings={validators.validatorSettings}>
    <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <SectionPanel.Section>
          <InvoiceV2DraftSection
            invoice={invoice}
            onInvoiceChange={setInvoice}
            refetchInvoice={refetchInvoice}
            validate={isValid}
            onBookkeepingOrCurrencyChange={onBookkeepingOrCurrencyChange}
            initialProductsByParent={props.viewModel.productsPanel.data}
            missingFields={missingFields}
            validateForPreview={validateForPreview}
          />

          <RecipientsPanel
            initialViewers={props.viewModel.recipients}
            invoice={invoice}
          />

          {isPanelVisible(props.viewModel.linkedShowsPanel.config) && <ReadonlyLinkedShowsPanelSegment
            documentType={"invoice"}
            linkedShowsPanel={props.viewModel.linkedShowsPanel}
          />}

          <ProductsPanel
            useSectionPanel
            documentType={"invoice"}
            documentId={invoice.invoiceId}
            productsPanel={props.viewModel.productsPanel}
            validatorKey={RESX.Product.Products}
            onRefetchedProducts={refetchInvoice}
          />

          <NotesPanel
            mainArea={"invoice"}
            useSectionPanel
            config={props.viewModel.linkedNotePanelConfig}
          />

          <SectionPanel.Panel>
            <ChangeLifecycleButtons
              documentType="invoice"
              documentId={invoice.invoiceId}
              right="review"
              rightValidate={isValid}
            />
          </SectionPanel.Panel>
        </SectionPanel.Section>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn size="medium">
        <InvoiceV2OptionsPanel
          viewModel={props.viewModel}
          invoice={invoice}
        />

        <InvoiceReminderApp
          invoice={invoice}
          initialReminders={props.viewModel.reminders}
        />

        <InvoiceActivityPanel documentId={invoice.invoiceId} config={props.viewModel.activityPanelConfig} />
      </TwoColumnLayout.RightColumn>
    </TwoColumnLayout>
  </ValidatorContext>
}

export default DraftPanel;
